<template>
  <div
    :class="[
      !data.isActive ? 'bg-gray-100 opacity-60' : 'bg-white',
      'border-b border-gray-200 last:border-b-0'
    ]"
  >
    <div
      class="grid lg:grid-cols-12 md:grid-cols-6 grid-cols-1 gap-2 w-full items-center py-2 my-0"
    >
      <div class="lg:col-span-2 md:col-span-2 col-span-12 flex flex-row md:flex-row">
        <span class="text-left font-bold md:hidden mr-2">ID:</span>
        <p class="md:text-left">#{{ data.id }}</p>
      </div>

      <div class="lg:col-span-3 md:col-span-4 col-span-12 flex flex-row md:flex-row">
        <span class="text-left font-bold md:hidden mr-2">Nome da Política:</span>
        <p class="md:text-left truncate">
          {{ data.name }}
        </p>
      </div>

      <div class="lg:col-span-2 md:col-span-3 col-span-12 flex flex-row md:flex-row">
        <span class="text-left font-bold md:hidden mr-2">Empresa:</span>
        <p class="md:text-left">{{ data.company.name }}</p>
      </div>

      <div
        class="lg:col-span-2 md:col-span-3 col-span-12 flex flex-row md:flex-row items-center md:items-start"
      >
        <span class="text-left font-bold md:hidden mr-2">Criado por:</span>
        <div class="flex justify-center md:justify-start">
          <UserDisplay :name="data.createdBy" />
        </div>
      </div>

      <div
        class="col-span-12 md:col-span-6 lg:col-span-3 flex flex-row lg:justify-between justify-center items-center lg:gap-4 gap-2 mt-4 md:mt-0"
      >
        <!-- <v-tooltip location="top">
          <template #activator="{ props }">
            <div v-bind="props" class="ml-4">
              <v-icon
                :icon="mdiShareVariantOutline"
                color="#2f7f91"
                class="w-8 h-8"
                :class="[canSharePolicy ? 'cursor-pointer' : 'opacity-40 pointer-events-none']"
                @click="canSharePolicy ? handleShare() : null"
              />
            </div>
          </template>
          <span>{{
            canSharePolicy ? 'Compartilhar política' : 'Você não tem permissão para compartilhar'
          }}</span>
        </v-tooltip> -->

        <v-tooltip location="top">
          <template #activator="{ props }">
            <div v-bind="props" class="ml-4">
              <EditIcon
                :class="[
                  !data.isActive || !canEditPolicy
                    ? 'opacity-40 pointer-events-none'
                    : 'cursor-pointer'
                ]"
                color="#2f7f91"
                class="w-8 h-8"
                @click="canEditPolicy ? handleEdit() : null"
              />
            </div>
          </template>
          <span v-if="!canEditPolicy">Você não tem permissão para editar</span>
          <span v-else-if="!data.isActive">Ative a política para editar</span>
          <span v-else>Editar política</span>
        </v-tooltip>

        <v-tooltip location="top">
          <template #activator="{ props }">
            <div v-bind="props" class="ml-4">
              <DuplicateIcon
                :class="[
                  !data.isActive || !canEditPolicy
                    ? 'opacity-40 pointer-events-none'
                    : 'cursor-pointer'
                ]"
                color="#2f7f91"
                class="w-8 h-8"
                @click="canEditPolicy ? handleDuplicate() : null"
              />
            </div>
          </template>
          <span v-if="!canEditPolicy">Você não tem permissão para duplicar</span>
          <span v-else-if="!data.isActive">Ative a política para duplicar</span>
          <span v-else>Duplicar política</span>
        </v-tooltip>

        <div class="w-12">
          <v-tooltip location="top">
            <template #activator="{ props }">
              <div
                v-bind="props"
                :class="[!canTogglePolicy ? 'cursor-not-allowed' : 'cursor-pointer']"
              >
                <DsSwitch
                  :disabled="!canTogglePolicy"
                  :model-value="policyDataActive"
                  @update:model-value="onToggle"
                />
              </div>
            </template>
            <span v-if="!canTogglePolicy">Você não tem permissão para alterar</span>
            <span v-else>{{ policyDataActive ? 'Desativar política' : 'Ativar política' }}</span>
          </v-tooltip>
        </div>
      </div>
    </div>
  </div>

  <DuplicateDsPolicyModal
    :modalActive="showDuplicateModal"
    :policy="selectedPolicyToDuplicate"
    @close="showDuplicateModal = false"
  />

  <ConfirmDeleteModal
    @closeModal="showDeleteModal = false"
    :show="showDeleteModal"
    :isLoading="isLoadingDelete"
    :title="`Atenção, confirme a desativação`"
    :text-button="`Desativar`"
    :description="`Você tem certeza que deseja desativar a política:  ${groupToDelete.name}?`"
  />
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import DsSwitch from '@/components/designSystem/Switch/index.vue'
import { useRouter } from 'vue-router'
import { useToast } from 'vue-toastification'
import { useErrorToast } from '@/composables/useErrorToast'

import PATHS from '@/router/paths'
import { useDsPolicyStore } from '@/pinia-store/useDsPolicyStore'

import { DsPolicyItem } from '@/Network/Types/Responses/DsPolicyResponses'

import EditIcon from '@/assets/icons/edit.vue'
import DuplicateIcon from '@/assets/icons/duplicate.vue'

import UserDisplay from '../AvatarUser/index.vue'
import ConfirmDeleteModal from '@/components/designSystem/modals/confirm.delete.action.vue'
import DuplicateDsPolicyModal from '@/modules/company/Policy/DsPolicy/components/DuplicateDsPolicyModal/Index.vue'
import { AuthorizationPermissions, AuthorizationResourcesAndSubjects } from '@/types/authorization'
import { usePermissionsStore } from '@/pinia-store/usePermissionsStore'

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const props = defineProps<{
  data: DsPolicyItem
}>()

// const { flags } = useFeatureFlags()
const permissionsStore = usePermissionsStore()

const isLoadingDelete = ref(false)
const showDeleteModal = ref(false)
const groupToDelete = ref<{ name: string; id: number }>({ id: 0, name: '' })
const showDuplicateModal = ref(false)
const selectedPolicyToDuplicate = ref<DsPolicyItem | null>(null)

const policyStore = useDsPolicyStore()
const router = useRouter()
const toast = useToast()
const { notifyError } = useErrorToast()

const canEditPolicy = computed(() => {
  return permissionsStore.checkAnyRessources({
    permission: AuthorizationPermissions.StationCreating,
    resourceType: AuthorizationResourcesAndSubjects.Schedule
  })
})

const canTogglePolicy = computed(() => {
  return permissionsStore.checkAnyRessources({
    permission: AuthorizationPermissions.StationCreating,
    resourceType: AuthorizationResourcesAndSubjects.Schedule
  })
})

const policyDataActive = computed(() => {
  const filteredPolicies = policyStore.policies.filter((policy) => policy.id === props.data.id)
  return filteredPolicies[0].isActive
})

const handleEdit = async () => {
  try {
    await policyStore.fetchDsPolicyById(props.data.id)
  } catch (e) {
    console.error('Erro ao buscar política para edição:', e)
    notifyError(e, 'Não foi possível abrir a política de restrição para edição.')
    return
  }
  router.push(`${PATHS.MANAGE_DOCTOR_AUTHORIZATION_POLICIES}/${props.data.id}`)
}

const handleDuplicate = () => {
  selectedPolicyToDuplicate.value = props.data
  showDuplicateModal.value = true
}

const onToggle = async (value: boolean) => {
  if (value) {
    try {
      await policyStore.enableDsPolicy(props.data.id)
      toast.success(`Política ${props.data.name} ativada com sucesso!`)
    } catch (e) {
      notifyError(e, 'Não foi possível ativar a política de restrição.')
    }
  } else {
    try {
      await policyStore.disableDsPolicy(props.data.id)
      toast.success(`Política ${props.data.name} desativada com sucesso!`)
    } catch (e) {
      notifyError(e, 'Não foi possível desativar a política de restrição.')
    }
  }
}
</script>
